import { panelNavHtml } from '../panel-nav-html.js';
import { downloadBriefPdf, type PdfBriefPayload } from '../pdf/brief-pdf.js';

/**
 * Discovery brief thank-you card. Fired by the intake variant's
 * submit_brief tool — the equivalent of the BookingConfirmed card in
 * the sales flow. Displays a warm "thanks, we'll be in touch" message
 * with a "Download brief (PDF)" CTA powered by jsPDF.
 *
 * Payload mirrors the server's brief_submitted event — the structured
 * brief plus a reference. We keep the structured form on the component
 * so the PDF download stays available even after the WS closes.
 */

type EmailDeliveryStatus = 'accepted' | 'not_sent' | 'unknown';
export type BriefSubmittedPayload = PdfBriefPayload & {
  emailDelivery?: { team: EmailDeliveryStatus; caller: EmailDeliveryStatus };
};

export class BriefSubmitted {
  private payload?: BriefSubmittedPayload;

  constructor(private readonly root: HTMLElement) {
    this.root.classList.add('brief-submitted-overlay');
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const action = t.closest<HTMLElement>('[data-action]')?.dataset.action;
      switch (action) {
        case 'close':
          this.hide();
          document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
          return;
        case 'back':
          document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
          return;
        case 'download-pdf': {
          if (!this.payload) return;
          // Lazy: the actual jsPDF library only loads on first click.
          // Disable the button while the chunk fetches so a second click
          // doesn't fire two downloads.
          const btn = t.closest<HTMLButtonElement>('[data-action="download-pdf"]');
          if (btn) btn.disabled = true;
          downloadBriefPdf(this.payload).finally(() => {
            if (btn) btn.disabled = false;
          });
          return;
        }
      }
    });
  }

  show(payload: BriefSubmittedPayload): void {
    this.payload = payload;
    this.root.innerHTML = this.render(payload);
    this.root.classList.add('visible');
  }

  clear(): void {
    this.payload = undefined;
    this.root.innerHTML = '';
    this.hide();
  }

  hide(): void {
    this.root.classList.remove('visible');
  }

  get isOpen(): boolean {
    return this.root.classList.contains('visible');
  }

  private render(p: BriefSubmittedPayload): string {
    const teamStatus = p.emailDelivery?.team ?? 'unknown';
    const callerStatus = p.emailDelivery?.caller ?? 'unknown';
    const teamMessage = teamStatus === 'accepted'
      ? 'The email provider accepted your brief for the team. Inbox delivery is not guaranteed.'
      : teamStatus === 'not_sent'
        ? 'Your brief is ready, but the email to the team was not sent. Download your copy before leaving.'
        : 'Your brief is ready, but email delivery to the team is unconfirmed. Download your copy before leaving.';
    const callerMessage = callerStatus === 'accepted'
      ? 'The email provider also accepted your copy.'
      : callerStatus === 'not_sent' ? 'Your email copy was not sent.' : 'Your email copy is unconfirmed.';
    const firstName = p.caller.name.split(/\s+/)[0] ?? p.caller.name;
    return `
      <div class="bs-card">
        ${panelNavHtml()}
        <div class="bs-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h3>Thanks, ${esc(firstName)}.</h3>
        <p class="bs-message">${teamMessage}</p>
        <div class="bs-meta">
          <span>Reference</span>
          <strong>${esc(p.reference)}</strong>
        </div>
        <div class="bs-meta">
          <span>Topic</span>
          <strong>${esc(p.feature.title)}</strong>
        </div>
        <p class="bs-message bs-message--muted">${callerMessage} This page keeps your PDF available until you leave or start another call.</p>
        <div class="bs-actions">
          <button class="bs-btn" data-action="download-pdf" type="button">
            <svg class="bs-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download brief (PDF)
          </button>
        </div>
      </div>`;
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
